"use client";

import { Badge } from "@/components/ui/badge";
import { Carousel, CarouselContent, CarouselItem } from "@/components/ui/carousel";
import { Award, CircleCheck, Shield } from "lucide-react";
import { useEffect, useState } from "react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function TrustEvidence() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.2,
    once: true
  });
  const [api, setApi] = useState<any>(null);
  useEffect(() => {
    if (!api) return;
    const interval = setInterval(() => {
      api.scrollNext();
    }, 5000);
    return () => clearInterval(interval);
  }, [api]);
  const trustBadges = [{
    icon: Award,
    title: "Qualified Physiotherapists",
    description: "Evidence-based treatment from experienced professionals"
  }, {
    icon: Shield,
    title: "Personalized Care Plans",
    description: "Tailored rehabilitation strategies for optimal recovery"
  }, {
    icon: CircleCheck,
    title: "Proven Recovery Methods",
    description: "Up-to-date clinical techniques backed by research"
  }];
  const testimonials = [{
    quote: "Axion Physiotherapy helped me recover quickly from my sports injury. Highly professional and friendly staff.",
    author: "Jane D.",
    position: "Active Adult Client"
  }, {
    quote: "Excellent care for my neck pain. The team is knowledgeable and caring.",
    author: "Mark S.",
    position: "Office Worker"
  }, {
    quote: "The personalized treatment plan made all the difference in my post-surgery rehabilitation.",
    author: "Sarah L.",
    position: "Post-Surgery Patient"
  }];
  return <section id="trust-evidence" className="dark bg-background text-foreground py-16 px-4 relative overflow-hidden">
      <div className="absolute inset-0 opacity-5" style={{
      backgroundImage: "repeating-linear-gradient(90deg, hsl(0, 0%, 100%) 0px, hsl(0, 0%, 100%) 1px, transparent 1px, transparent 4px)"
    }} />

      <div ref={ref} className={`max-w-7xl mx-auto grid md:grid-cols-2 gap-12 relative transition-all duration-300 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-5"}`}>
        <div className="space-y-8">
          <div className="space-y-2">
            <Badge variant="outline" className="border-primary/40 text-primary bg-primary/10">
              Trusted Care
            </Badge>
            <h2 className="text-4xl font-display font-bold text-foreground">
              Evidence-Based Excellence
            </h2>
          </div>

          <div className="space-y-6">
            {trustBadges.map((badge, i) => {
            const Icon = badge.icon;
            return <div key={i} data-index={i} className="flex gap-4 items-start p-6 rounded-md bg-secondary/5 border border-border/50 hover:border-primary/30 transition-all duration-300">
                  <div className="flex-shrink-0 w-12 h-12 rounded-md bg-primary/10 flex items-center justify-center">
                    <Icon className="w-6 h-6 text-primary" strokeWidth={2} />
                  </div>
                  <div className="space-y-1">
                    <h3 className="text-lg font-display font-semibold text-foreground">
                      {badge.title}
                    </h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {badge.description}
                    </p>
                  </div>
                </div>;
          })}
          </div>
        </div>

        <div className="flex items-center">
          <div className="w-full">
            <div className="space-y-2 mb-6">
              <Badge variant="outline" className="border-accent/40 text-accent-foreground bg-accent/10">
                Client Testimonials
              </Badge>
              <h3 className="text-2xl font-display font-bold text-foreground">
                What Our Clients Say
              </h3>
            </div>

            <Carousel setApi={setApi} opts={{
            align: "start",
            loop: true
          }} className="w-full">
              <CarouselContent>
                {testimonials.map((testimonial, i) => <CarouselItem key={i} data-index={i}>
                    <div className="p-8 rounded-md bg-secondary/5 border border-border/50 space-y-4 min-h-[200px] flex flex-col justify-between">
                      <p className="text-base text-muted-foreground italic leading-relaxed">
                        "{testimonial.quote}"
                      </p>
                      <div className="space-y-1">
                        <p className="font-semibold text-foreground text-sm">
                          {testimonial.author}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {testimonial.position}
                        </p>
                      </div>
                    </div>
                  </CarouselItem>)}
              </CarouselContent>
            </Carousel>

            <div className="flex justify-center gap-2 mt-6">
              {testimonials.map((_, i) => <button key={i} data-index={i} onClick={() => api?.scrollTo(i)} className="w-2 h-2 rounded-full bg-muted-foreground/30 hover:bg-primary transition-all duration-300" aria-label={`Go to testimonial ${i + 1}`} />)}
            </div>
          </div>
        </div>
      </div>
    </section>;
}